import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";



export function ServerConfig() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [port, setPort] = useState("");
  const [hooks, setHooks] = useState(null);
  const [threshold, setThreshold] = useState("");
  const [drafts, setDrafts] = useState({ pvp_alerts: "", trade_alerts: "", chat_bridge: "", master_trainer: "" });
  // Daily Hunt config — kept as a separate draft object so the user can
  // tweak inputs and instantly see the projected per-day payout table
  // before saving.
  const [huntCfg, setHuntCfg] = useState(null);
  const [huntDraft, setHuntDraft] = useState(null);

  const load = () => api.get("/admin/server-config/list").then(({ data }) => setRows(data.servers || [])).catch(() => {});
  const loadHooks = () => api.get("/admin/event-webhooks").then(({ data }) => {
    setHooks(data);
    setThreshold(String(data.trade_alert_threshold ?? 100000));
  }).catch(() => {});
  const loadHuntCfg = () => api.get("/admin/hunt-config").then(({ data }) => {
    setHuntCfg(data);
    setHuntDraft({ ...data.config });
  }).catch(() => {});
  useEffect(() => { load(); loadHooks(); loadHuntCfg(); }, []);

  const save = async () => {
    try {
      await api.put("/admin/server-config/game-port", { server_name: editing, game_port: parseInt(port, 10) });
      toast.success(`Game port set to ${port}`);
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveHook = async (kind) => {
    try {
      await api.put(`/admin/event-webhooks/${kind}`, { webhook_url: drafts[kind] });
      toast.success(`${kind.replace("_", " ")} webhook saved`);
      setDrafts((d) => ({ ...d, [kind]: "" }));
      loadHooks();
    } catch (e) { toast.error(apiError(e)); }
  };

  // One-off URL ping so the admin can verify the configured webhook
  // actually reaches Discord. Backend returns {posted, skipped, error}
  // — we surface whichever applies in a toast so the admin gets
  // immediate visual feedback (and the audit log records it too).
  const testHook = async (kind) => {
    try {
      const { data } = await api.post(`/admin/event-webhooks-test/${kind}`);
      if (data.posted) {
        toast.success(`Test ping delivered to ${kind.replace("_", " ")} ✅`);
      } else if (data.skipped) {
        toast.error(`Skipped: ${data.skipped}`);
      } else {
        toast.error(`Failed: ${data.error || "unknown error"}`);
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveThreshold = async () => {
    try {
      await api.put("/admin/event-webhooks/trade-threshold", { threshold: parseInt(threshold, 10) || 0 });
      toast.success("Large-trade threshold updated");
      loadHooks();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveHuntCfg = async () => {
    if (!huntDraft) return;
    try {
      await api.put("/admin/hunt-config", {
        base_reward:     parseInt(huntDraft.base_reward, 10) || 0,
        all_bonus:       parseInt(huntDraft.all_bonus, 10) || 0,
        max_streak_days: parseInt(huntDraft.max_streak_days, 10) || 1,
        daily_picks:     parseInt(huntDraft.daily_picks, 10) || 1,
        radius_game:     parseFloat(huntDraft.radius_game) || 0,
      });
      toast.success("Hunt rewards updated");
      loadHuntCfg();
    } catch (e) { toast.error(apiError(e)); }
  };
  const resetHuntCfg = () => {
    if (huntCfg) setHuntDraft({ ...huntCfg.defaults });
  };
  const huntField = (k, v) => setHuntDraft((d) => ({ ...d, [k]: v }));

  // Compute streak preview table from the live draft so the admin can see
  // exactly what each consecutive day will pay out before clicking Save.
  const huntPreview = (() => {
    if (!huntDraft) return [];
    const base = parseInt(huntDraft.base_reward, 10) || 0;
    const bonus = parseInt(huntDraft.all_bonus, 10) || 0;
    const maxDays = Math.max(1, parseInt(huntDraft.max_streak_days, 10) || 1);
    const picks = Math.max(1, parseInt(huntDraft.daily_picks, 10) || 1);
    const span = Math.max(1, maxDays - 1);
    const out = [];
    for (let day = 1; day <= maxDays; day += 1) {
      const mult = 1 + Math.min(1, (day - 1) / span);
      const perHunt = Math.floor(base * mult);
      const allBonus = Math.floor(bonus * mult);
      out.push({
        day,
        mult: mult.toFixed(2),
        perHunt,
        allBonus,
        dailyMax: perHunt * picks + allBonus,
      });
    }
    return out;
  })();

  return (
    <div className="space-y-4 fade-up" data-testid="server-config-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Server Config</h3>
        <p className="text-xs text-[var(--muted)]">
          Read-only view of <code>servers</code> table + game-port editor (used by SteamCMD / browser-query). RCON / connection ports live in the bot's config — not editable here.
        </p>
      </div>
      <div className="glass-card p-5" data-testid="server-config-list">
        <h3 className="label mb-3">Servers ({rows.length})</h3>
        <ul className="divide-y divide-white/5">
          {rows.map((r) => (
            <li key={r.server_name} className="py-3 text-sm" data-testid={`sc-row-${r.server_name}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{r.server_name}</div>
                  <div className="text-[10px] text-[var(--muted)] font-mono">
                    {r.server_host} · rcon {r.rcon_port} · conn {r.connection_port} · game <strong>{r.game_port}</strong>
                  </div>
                </div>
                <button onClick={() => { setEditing(r.server_name); setPort(r.game_port); }} className="btn btn-sm btn-secondary" data-testid={`sc-edit-${r.server_name}`}>Edit Port</button>
              </div>
              {editing === r.server_name && (
                <div className="mt-3 flex items-end gap-2" data-testid={`sc-edit-form-${r.server_name}`}>
                  <Field label="Game Port"><input className="input" type="number" min="1" max="65535" value={port} onChange={(e) => setPort(e.target.value)} data-testid="sc-port-input" /></Field>
                  <button onClick={save} className="btn" data-testid="sc-port-save">Save</button>
                  <button onClick={() => setEditing(null)} className="btn btn-secondary" data-testid="sc-port-cancel">Cancel</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="glass-card p-5" data-testid="event-webhooks">
        <h3 className="label mb-1">Discord Event Webhooks</h3>
        <p className="text-xs text-[var(--muted)] mb-4">
          Get a Discord notification when significant in-portal events happen. Paste the channel webhook URL (Server Settings → Integrations → Webhooks). Leave blank to disable.
          <br />
          <span className="text-[10px]">
            <strong className="text-[var(--bone)]">Tip:</strong>{" "}
            <strong>chat_bridge</strong> is the webhook used by the website's
            cross-chat widget — point it at the same channel your bot posts
            game chat to, and website messages will land there too.
          </span>
        </p>
        {hooks ? (
          <div className="space-y-4">
            {["pvp_alerts", "trade_alerts", "chat_bridge", "master_trainer"].map((kind) => (
              <div key={kind} data-testid={`hook-row-${kind}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="capitalize">{kind.replace("_", " ")}</span>
                  <span className={`text-[10px] ${hooks[kind].configured ? "text-[var(--teal)]" : "text-[var(--muted)]"}`}>
                    {hooks[kind].configured ? `set · ${hooks[kind].url_masked}` : "not configured"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input
                    className="input flex-1"
                    placeholder="https://discord.com/api/webhooks/..."
                    value={drafts[kind]}
                    onChange={(e) => setDrafts((d) => ({ ...d, [kind]: e.target.value }))}
                    data-testid={`hook-input-${kind}`}
                  />
                  <button onClick={() => saveHook(kind)} className="btn btn-sm" data-testid={`hook-save-${kind}`}>Save</button>
                  <button
                    onClick={() => testHook(kind)}
                    disabled={!hooks[kind].configured}
                    className="btn btn-sm btn-ghost"
                    title={hooks[kind].configured ? "Send a test ping to this webhook" : "Save a URL first"}
                    data-testid={`hook-test-${kind}`}
                  >
                    Test
                  </button>
                </div>
              </div>
            ))}
            <div className="pt-3 border-t border-white/5">
              <div className="text-sm mb-1">Large-trade alert threshold (skulls)</div>
              <div className="text-[10px] text-[var(--muted)] mb-2">Trades whose total value (offer + request currency) hits this number fire a Discord embed. Default: 100,000.</div>
              <div className="flex flex-wrap gap-2">
                <input
                  type="number"
                  min="0"
                  className="input flex-1"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  data-testid="hook-threshold-input"
                />
                <button onClick={saveThreshold} className="btn btn-sm" data-testid="hook-threshold-save">Save</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-[var(--muted)]">Loading…</div>
        )}
      </div>

      <div className="glass-card p-5" data-testid="hunt-config-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="label !mb-0">Daily Hunt Rewards</h3>
          {huntCfg && (
            <button onClick={resetHuntCfg} className="text-[10px] text-[var(--muted)] hover:text-[var(--teal)]" data-testid="hunt-reset-defaults">Reset to defaults</button>
          )}
        </div>
        <p className="text-[10px] text-[var(--muted)] mb-4">
          Tune the auto-claim treasure-hunt payouts. Changes take effect within ~30 s (in-memory cache TTL); the preview table below recomputes live as you type.
        </p>
        {huntDraft ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Base reward (per hunt)">
                <input type="number" min="0" max="100000" className="input" value={huntDraft.base_reward} onChange={(e) => huntField("base_reward", e.target.value)} data-testid="hunt-input-base-reward" />
              </Field>
              <Field label="All-3 bonus">
                <input type="number" min="0" max="1000000" className="input" value={huntDraft.all_bonus} onChange={(e) => huntField("all_bonus", e.target.value)} data-testid="hunt-input-all-bonus" />
              </Field>
              <Field label="Days to max streak">
                <input type="number" min="1" max="60" className="input" value={huntDraft.max_streak_days} onChange={(e) => huntField("max_streak_days", e.target.value)} data-testid="hunt-input-max-streak" />
              </Field>
              <Field label="Hunts per day">
                <input type="number" min="1" max="10" className="input" value={huntDraft.daily_picks} onChange={(e) => huntField("daily_picks", e.target.value)} data-testid="hunt-input-daily-picks" />
              </Field>
              <Field label="Claim radius (m)">
                <input type="number" min="50" max="2000" className="input" value={Math.round((huntDraft.radius_game || 0) / 100)} onChange={(e) => huntField("radius_game", (parseFloat(e.target.value) || 0) * 100)} data-testid="hunt-input-radius" />
              </Field>
            </div>

            <div className="overflow-x-auto" data-testid="hunt-preview-table">
              <table className="w-full text-xs">
                <thead className="text-[var(--muted)] uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-1.5 pr-3 text-left">Day</th>
                    <th className="py-1.5 px-2 text-right">Multiplier</th>
                    <th className="py-1.5 px-2 text-right">Per hunt</th>
                    <th className="py-1.5 px-2 text-right">All-3 bonus</th>
                    <th className="py-1.5 pl-2 text-right">Daily max</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {huntPreview.map((r) => (
                    <tr key={r.day} className="border-t border-white/5">
                      <td className="py-1.5 pr-3">{r.day === huntPreview.length ? `${r.day}+` : r.day}</td>
                      <td className="py-1.5 px-2 text-right">{r.mult}×</td>
                      <td className="py-1.5 px-2 text-right">{r.perHunt.toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right">{r.allBonus.toLocaleString()}</td>
                      <td className="py-1.5 pl-2 text-right text-[var(--teal)]"><span className="inline-flex items-center gap-1 justify-end">{r.dailyMax.toLocaleString()} <ChilletCoinIcon size={11}/></span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button onClick={saveHuntCfg} className="btn w-full" data-testid="hunt-save">Save Hunt Rewards</button>
          </div>
        ) : (
          <div className="text-xs text-[var(--muted)]">Loading…</div>
        )}
      </div>
    </div>
  );
}
